import { useCallback, useEffect, useState } from 'react'
import {
  fetchRoomDetail, formatCount, formatCredits, formatDayMonth, formatMinutes,
  formatTime, isOverCapacity, may, nameIn, peopleInRoom, pluralise,
  translateError, unitIn,
  type AppLocale, type RoomDetailView,
} from '@hk/core'
import {
  Button, Card, CardBody, CardHeader, ErrorState, Field, FieldInput, Identifier,
  LoadingState, useLocale,
} from '@hk/ui'
import { ArrowLeft, SprayCan } from 'lucide-react'
import { STRINGS } from '../i18n/strings'
import { RoomHistory } from './RoomHistory'
import { TaskDetail } from './TaskDetail'
import { type RoomBoardState } from './useRoomBoard'

/** The board's four visual tones, keyed by the state the server sends. The same mapping
 *  `boardFilters` makes for the tile, so the word on the card is the word on the board. */
const TONE = {
  DIRTY: 'dirty', IN_PROGRESS: 'prog', CLEAN: 'clean', INSPECTED: 'ready',
} as const

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-21 — THE ROOM.  Sprint 62.

   ── THE SAME DATA AS M-03 AND A DIFFERENT SCREEN ───────────────────────────────
   The handset already draws `RoomDetailView`; this is the first time the desk has. The
   two are NOT the same screen at two widths, and building it that way would have been
   the easy mistake:

   M-03 is read standing in a corridor with a trolley, before a door is opened, and it is
   ordered by what to carry. **This is read because somebody asked about a room** — the
   front desk with a guest at the counter, a supervisor working out why 207 is still not
   ready. So it is ordered by the question that was asked: what state is it in, who is in
   it, what work is outstanding, and then what has been done to it.

   ── IT REPLACED A DIALOG, AND THAT WAS THE POINT ───────────────────────────────
   The board's tile used to open a dialog that had quietly become three screens: the T8
   override, taking a room off sale, and the history. Everything it did is here, with the
   room's actual facts around it — a supervisor deciding whether to force 110 clean can
   now see that a guest arrives at 14:00 and it was rejected twice last week, which is the
   context that decision was always missing.
   ========================================================================= */

export function RoomDetail({
  roomId, board, onClose,
}: {
  roomId: string
  /** The board's own mutations, so the act and the list it changes stay one thing. */
  board: RoomBoardState
  onClose: () => void
}) {
  const { locale } = useLocale()
  /* **W-31 OPENS OVER THIS SCREEN** (Sprint 80). Held here rather than in the app
     shell because the way back from a task is the room it belongs to, and a
     nav key would lose that. */
  const [openTaskId, setOpenTaskId] = useState<string>()
  const t = STRINGS[locale]

  const [view, setView] = useState<RoomDetailView>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(true)
  const [epoch, setEpoch] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setView(await fetchRoomDetail(roomId))
      setError(undefined)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [roomId])

  useEffect(() => { void load() }, [load, epoch])

  /* **W-31 OPENS OVER THIS SCREEN AND CLOSES BACK ONTO IT.** Before the loading guard,
     so that closing a task does not put a spinner in front of the room somebody was
     already looking at. */
  if (openTaskId) {
    return <TaskDetail taskId={openTaskId} onClose={() => setOpenTaskId(undefined)} />
  }

  if (loading && !view) return <LoadingState />

  if (!view) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => void load()}
      />
    )
  }

  const people = peopleInRoom(view.adultCount, view.childCount)

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          <ArrowLeft className="size-3.5 rtl:rotate-180" aria-hidden />
          {t.rdBack}
        </Button>
        <h1 className="text-lg font-semibold">
          {t.room} <Identifier className="text-lg">{view.roomNumber}</Identifier>
        </h1>
        <span className="text-sm text-muted-foreground">
          {[
            nameIn(locale, view.roomTypeName, view.roomTypeNameAr),
            fill(t.rdFloor, { code: view.floorCode }),
            nameIn(locale, view.buildingName, view.buildingNameAr),
          ].join(' · ')}
        </span>
        {/* **WHERE THE ROOM IS IN ITS CLEANING**, which is the question the front desk is
            asked at the counter and the one this card could not answer until Sprint 62
            put `housekeeping` on the wire. The board's own words, from the board's own
            dictionary, because a room reading "Inspected" on one screen and "Ready" on
            the other is two products. */}
        <span
          className="rounded border px-1.5 py-0.5 text-[11px] font-bold"
          style={{
            color: `var(--hk-hk-${TONE[view.housekeeping]}-text)`,
            borderColor: `var(--hk-hk-${TONE[view.housekeeping]}-border)`,
          }}
        >
          {t.tones[TONE[view.housekeeping]]}
        </span>

        {/* **VIP IS A FACT ABOUT THE GUEST AND NOT A PRIORITY.** It is drawn beside the
            room and nowhere else on this screen: nothing here sorts by it, and a flag
            that moved work up a queue would be a decision this product never agreed. */}
        {view.vipFlag && (
          <span className="rounded border border-[var(--hk-occ-arr-fg)] px-1.5 py-0.5
                           text-[11px] font-bold"
                style={{ color: 'var(--hk-occ-arr-fg)' }}>
            {t.rdVip}
          </span>
        )}
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <GuestCard view={view} people={people} />
          <WorkCard view={view} onOpenTask={setOpenTaskId} />
          {view.linen.length > 0 && <LinenCard view={view} />}
          {view.cleaningNotes && (
            <Card>
              <CardHeader title={t.rdAboutRoom} />
              {/* **THEIR OWN WORDS, IN THEIR OWN DIRECTION.** A note typed in Arabic on
                  an English screen must not have its full stop thrown to the far end. */}
              <CardBody>
                <p className="text-sm" dir="auto" style={{ unicodeBidi: 'isolate' }}>
                  {view.cleaningNotes}
                </p>
              </CardBody>
            </Card>
          )}
          {view.lastClean && <LastCleanCard view={view} />}
        </div>

        <div className="space-y-4">
          <Card>
            <CardBody>
              <RoomHistory roomId={roomId} t={t} locale={locale} />
            </CardBody>
          </Card>

          <Acts
            view={view}
            board={board}
            onDone={() => { setEpoch((n) => n + 1); board.refetch() }}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * **WHO IS IN IT, AND WHEN.** First, because it is the only thing on this screen with a
 * deadline in it, and because it is what the person at the counter was asked.
 */
function GuestCard(
  { view, people }: { view: RoomDetailView; people: number | null },
) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <Card>
      <CardHeader title={t.rdGuest} />
      <CardBody className="space-y-2">
        <p className="text-sm font-medium">{occupancySentence(view, locale, t)}</p>

        {/* **THE TWO NUMBERS TOGETHER OR NEITHER** — M-03's rule, and it is the same rule
            because it is the same fact. "Three people" is an ordinary stay in a room for
            four and a mattress on the floor in a room for two. Drawn only where the
            property system has actually counted: `peopleInRoom` returns null rather than
            zero, because "0 people" against a room a family is standing in is a lie the
            product would be telling in its own voice. */}
        {people !== null && (
          <p className="text-sm text-muted-foreground">
            {[
              pluralise(t.rdPeopleIn, people, locale, formatCount(people, locale)),
              fill(t.rdSleeps, { count: formatCount(view.capacity, locale) }),
            ].join(' · ')}
          </p>
        )}

        {isOverCapacity(view.capacity, view.adultCount, view.childCount) && (
          <p className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
            {fill(t.rdOverCapacity, { count: formatCount(people ?? 0, locale) })}
          </p>
        )}

        {view.departureUtc && (
          <p className="text-sm text-muted-foreground">
            {fill(t.rdLeavingAt, {
              time: formatTime(new Date(view.departureUtc), locale),
            })}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/** **WHAT IS OUTSTANDING**, and the checklist it will be cleaned against. */
function WorkCard(
  { view, onOpenTask }: { view: RoomDetailView; onOpenTask?: (taskId: string) => void },
) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const task = view.task

  if (!task) {
    return (
      <Card>
        <CardHeader title={t.rdWork} />
        <CardBody className="space-y-1">
          <p className="text-sm font-medium">{t.rdNothingDue}</p>
          <p className="text-sm text-muted-foreground">{t.rdNothingDueBody}</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title={t.rdWork} />
      <CardBody className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-medium">
            {nameIn(locale, task.taskTypeName, task.taskTypeNameAr)}
          </span>
          <span className="tnum text-sm text-muted-foreground">
            {fill(t.rdCredits, { credits: formatCredits(task.credits, locale) })}
          </span>
        </div>

        <p className="text-sm text-muted-foreground">
          {[
            t.rdStates[task.state],
            fill(t.rdEstimated, {
              minutes: formatMinutes(task.estimatedMinutes, locale),
            }),
          ].join(' · ')}
        </p>

        {task.firstStartedAtUtc && (
          <p className="text-sm text-muted-foreground">
            {fill(t.rdStartedAt, {
              time: formatTime(new Date(task.firstStartedAtUtc), locale),
            })}
          </p>
        )}

        {/* **W-31, AND THE ONLY WAY INTO IT** (Sprint 80). Everything above is a summary;
            this is where somebody goes when the summary is not the question. */}
        {onOpenTask && (
          <button
            type="button"
            onClick={() => onOpenTask(task.taskId)}
            className="text-start text-sm underline underline-offset-2"
          >
            {t.rdOpenTask}
          </button>
        )}

        {/* **"WILL FOLLOW" AND "WOULD FOLLOW" ARE TWO DIFFERENT PROMISES**, and the
            server distinguishes them with one boolean that nothing has ever drawn. A task
            already bound to a version WILL be cleaned against it, whatever is published
            tomorrow; an unstarted one merely WOULD follow the newest. Saying "will" for
            both is how somebody edits a checklist, expects the room in progress to
            change, and is quietly wrong. */}
        {view.checklist && (
          <p className="text-sm text-muted-foreground">
            {fill(view.checklist.pinned ? t.rdWillFollow : t.rdWouldFollow, {
              name: nameIn(
                locale, view.checklist.templateName, view.checklist.templateNameAr),
              version: formatCount(view.checklist.versionNumber, locale),
              items: formatCount(view.checklist.itemCount, locale),
            })}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/** **WHAT THE ROOM NEEDS.** The linen standard for this room type and this kind of clean. */
function LinenCard({ view }: { view: RoomDetailView }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <Card>
      <CardHeader title={t.rdNeeds} />
      <CardBody className="space-y-1.5">
        {view.linen.map((line) => (
          <div
            key={line.stockItemCode}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span>{nameIn(locale, line.name, line.nameAr)}</span>
            {/* The count and the unit together, in the reader's own numerals. */}
            <span className="tnum font-medium">
              {formatCount(line.quantity, locale)} {unitIn(locale, line.unit)}
            </span>
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

/** **WHAT HAPPENED HERE LAST TIME** — the same two sentences M-03 draws. */
function LastCleanCard({ view }: { view: RoomDetailView }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const last = view.lastClean!

  /* BOTH NAMES WHEN A PAIR CLEANED IT. A team has no name in this product — the client's
     own decision — so the two men ARE the name, and crediting one of them is how the
     other stops believing the screen. */
  const who = last.byName
    ? last.byNameTwo
      ? `${nameIn(locale, last.byName, last.byNameAr)} + ${nameIn(locale, last.byNameTwo, last.byNameTwoAr)}`
      : nameIn(locale, last.byName, last.byNameAr)
    : null

  const line = [
    fill(t.rdCleanedOn, {
      date: formatDayMonth(new Date(last.completedAtUtc), locale),
    }),
    ...(who ? [fill(t.rdByPerson, { name: who })] : []),
    ...(last.minutes !== null ? [formatMinutes(last.minutes, locale)] : []),
  ].join(' · ')

  /* **NULL IS A REAL ANSWER: nobody inspected it.** Not the same as failing, and the
     sentence must not read like an accusation of a man who was never checked. */
  const inspection =
    last.passedFirstTime === null ? t.rdNotInspected
    : last.passedFirstTime ? t.rdPassedFirstTime
    : t.rdWasRejected

  return (
    <Card>
      <CardHeader title={t.rdLastTime} />
      <CardBody className="space-y-1">
        <p className="text-sm text-muted-foreground"><bdi>{line}</bdi></p>
        <p className="text-sm text-muted-foreground">{inspection}</p>
      </CardBody>
    </Card>
  )
}

/**
 * **THE TWO ACTS THE DIALOG USED TO CARRY.** Each behind its own permission, each with
 * its own reason box — deliberately not one box shared between them, because they are two
 * sentences about two different acts and a box that kept the words typed for one and sent
 * them with the other would put a false reason in the activity log. That log is the only
 * reason either act is permitted at all.
 */
function Acts({
  view, board, onDone,
}: {
  view: RoomDetailView
  board: RoomBoardState
  onDone: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const mayOverride = may('room.overrideToClean')
  const maySetService = may('room.setOutOfService')

  const [reason, setReason] = useState('')
  const [serviceReason, setServiceReason] = useState('')

  const offSale = view.service === 'OUT_OF_SERVICE'
  const outOfOrder = view.service === 'OUT_OF_ORDER'
  const isDirty = view.housekeeping === 'DIRTY'

  if (!mayOverride && !maySetService) return null

  /* THE SERVER REFUSES A BLANK REASON AND SO DOES THIS — two guards on one rule rather
     than one guard in the wrong place. The server's is the enforcement; this one exists so
     nobody types nothing, presses, waits, and is told off. */
  const overrideReady = isDirty && reason.trim().length > 0 && !board.markingClean
  const serviceReady =
    serviceReason.trim().length > 0 && !board.settingService && !outOfOrder

  return (
    <Card>
      <CardHeader title={t.rdActs} />
      <CardBody className="space-y-4">
        {mayOverride && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {fill(t.ovrTitle, { room: view.roomNumber })}
            </p>
            <p className="text-xs text-muted-foreground">
              {fill(t.ovrIntro, { room: view.roomNumber })}
            </p>

            {/* AMBER AND NOT RED. Nothing is broken and nobody has done anything wrong:
                they opened a room this act does not apply to. */}
            {!isDirty ? (
              <p role="status" className="rounded-hk border border-[#fcd34d] bg-[#fffbeb]
                                          px-3 py-2 text-xs text-[#92400e]
                                          dark:border-[#b45309] dark:bg-[#78350f]/25
                                          dark:text-[#fcd34d]">
                {t.ovrOnlyDirty}
              </p>
            ) : (
              <>
                <Field label={t.ovrReasonLabel} hint={t.ovrReasonHint}>
                  {({ id, 'aria-label': label }) => (
                    <FieldInput
                      id={id}
                      aria-label={label}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  )}
                </Field>
                {board.markCleanError ? (
                  <p role="alert" className="text-xs text-[var(--hk-sv-ooo-text)]">
                    {String(translateError(board.markCleanError, locale).message)}
                  </p>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  disabled={!overrideReady}
                  onClick={() => {
                    if (!overrideReady) return
                    void board.markClean(view.roomId, reason.trim())
                      .then((ok) => { if (ok) { setReason(''); onDone() } })
                  }}
                >
                  <SprayCan className="size-3.5" aria-hidden />
                  {t.ovrConfirm}
                </Button>
              </>
            )}
          </div>
        )}

        {mayOverride && maySetService && <hr className="border-border" />}

        {maySetService && (
          <div className="space-y-2">
            {/* **WHAT IT IS NOW, BEFORE WHAT YOU CAN DO TO IT.** One control with two
                directions needs the current state beside it, or it is a button somebody
                presses to find out what it does. */}
            {offSale && (
              <p className="text-sm font-medium" style={{ color: 'var(--hk-sv-oos-text)' }}>
                {t.svcOffNow}
              </p>
            )}
            <p className="text-sm font-medium">
              {fill(offSale ? t.svcOnTitle : t.svcOffTitle, { room: view.roomNumber })}
            </p>
            <p className="text-xs text-muted-foreground">
              {fill(offSale ? t.svcOnIntro : t.svcOffIntro, { room: view.roomNumber })}
            </p>

            {/* **A ROOM A FAULT TOOK OUT OF ORDER COMES BACK BY FIXING THE FAULT**, which
                is a different act with a different record. Said here rather than left to a
                refusal, so somebody learns where to go instead of only that they may not. */}
            {outOfOrder ? (
              <p role="status" className="rounded-hk border border-border bg-muted/40
                                          px-3 py-2 text-xs text-muted-foreground">
                {t.svcOutOfOrderNote}
              </p>
            ) : (
              <>
                <Field
                  label={offSale ? t.svcOnReasonLabel : t.svcOffReasonLabel}
                  hint={offSale ? t.svcOnReasonHint : t.svcOffReasonHint}
                >
                  {({ id, 'aria-label': label }) => (
                    <FieldInput
                      id={id}
                      aria-label={label}
                      value={serviceReason}
                      onChange={(e) => setServiceReason(e.target.value)}
                    />
                  )}
                </Field>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!serviceReady}
                  onClick={() => {
                    if (!serviceReady) return
                    void board.setService(view.roomId, !offSale, serviceReason.trim())
                      .then((ok) => { if (ok) { setServiceReason(''); onDone() } })
                  }}
                >
                  {offSale ? t.svcPutBack : t.svcTakeOff}
                </Button>
              </>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

type Dict = (typeof STRINGS)['en']

/**
 * **THREE DIFFERENT FACTS, THREE DIFFERENT SENTENCES** — M-03's rule, and the third is
 * the one that matters. "Arrival time not known" is NOT "no arrival today": a clerk told
 * the second one promises the room for the afternoon, and the guest arrives at eleven.
 */
function occupancySentence(view: RoomDetailView, locale: AppLocale, t: Dict): string {
  if (view.occupancy === 'OCCUPIED') return t.rdGuestInResidence

  /* **A DEPARTING ROOM IS NOT AN EMPTY ONE, AND SAYING "NO ARRIVAL TODAY" ABOUT IT IS
     TRUE AND USELESS.** The guest is still in it until they go; that is what the clerk at
     the counter is being asked about, and it is what decides whether the room can be
     promised to anybody. The four states get four sentences. */
  if (view.occupancy === 'DEPARTING') return t.rdGuestLeaving
  if (view.occupancy !== 'ARRIVING') return t.rdNoArrivalToday
  if (!view.arrivalUtc) return t.rdArrivalUnknown

  const at = fill(t.rdArrivingAt, {
    time: formatTime(new Date(view.arrivalUtc), locale),
  })
  if (!view.adultCount) return at

  /* THE DUAL IS NOT DECORATION. Arabic has a form for exactly two, and two adults is the
     commonest booking in this hotel. */
  const who = pluralise(
    t.rdAdults, view.adultCount, locale, formatCount(view.adultCount, locale))
  return `${at} · ${who}`
}
